<p-toast />
<p-confirmdialog [closable]="false" [closeOnEscape]="false" />
<app-page-header [title]="t()('menu.fileExplorer')" />

<section class="file-workspace" [class.preview-expanded]="previewExpanded()" [attr.aria-label]="t()('menu.fileExplorer')">
    <header class="explorer-toolbar">
        <div class="explorer-heading">
            <span class="heading-icon"><i class="fa fa-folder-open" aria-hidden="true"></i></span>
            <div>
                <h2>{{ breadcrumbs()[breadcrumbs().length - 1].label }}</h2>
                <p>{{ t()('files.subtitle') }}</p>
            </div>
        </div>
        <div class="toolbar-actions">
            <p-button icon="fa fa-arrow-up" severity="secondary" [text]="true" size="small" [ariaLabel]="t()('files.up')" [pTooltip]="t()('files.up')" [disabled]="!currentPath() || busy()" (onClick)="goUp()" />
            <p-button icon="fa fa-refresh" severity="info" [text]="true" size="small" [ariaLabel]="t()('files.refresh')" [pTooltip]="t()('files.refresh')" [loading]="loading()" [disabled]="busy()" (onClick)="refresh()" />
            <p-button
                icon="fa fa-upload"
                severity="success"
                [text]="true"
                size="small"
                [ariaLabel]="t()('files.upload')"
                [pTooltip]="t()('files.upload')"
                [loading]="uploading()"
                [disabled]="busy() || loading() || !!error()"
                (onClick)="fileInput.click()"
            />
            <input #fileInput type="file" multiple hidden (change)="onFileInput($event)" />
        </div>
    </header>

    <nav class="path-bar" [attr.aria-label]="t()('files.path')">
        @for (item of breadcrumbs(); track item.path) {
            @if (!$first) {
                <i class="fa fa-angle-right path-separator" aria-hidden="true"></i>
            }
            <button type="button" [class.current]="item.path === currentPath()" [attr.aria-current]="item.path === currentPath() ? 'location' : null" [disabled]="busy()" [title]="item.path || rootDirectory()" (click)="navigateTo(item.path)">
                @if ($first) {
                    <i class="fa fa-hard-drive" aria-hidden="true"></i>
                }
                {{ item.label }}
            </button>
        }
    </nav>

    <div class="explorer-panes">
        <section
            class="browser-pane"
            [class.drag-over]="isDragOver()"
            (dragover)="onFilesDragOver($event)"
            (dragleave)="onFilesDragLeave($event)"
            (drop)="onFilesDrop($event)"
            [attr.aria-label]="t()('files.files')"
            [attr.aria-busy]="loading() || uploading()"
        >
            <div class="browser-controls">
                <div class="search-field">
                    <i class="fa fa-search" aria-hidden="true"></i>
                    <input pInputText type="search" [ngModel]="search()" (ngModelChange)="search.set($event)" [placeholder]="t()('files.search')" [attr.aria-label]="t()('files.search')" />
                </div>
                <select class="sort-select" [ngModel]="sort()" (ngModelChange)="sort.set($event)" [attr.aria-label]="t()('files.sort')">
                    <option value="name">{{ t()('files.sortName') }}</option>
                    <option value="modified">{{ t()('files.sortModified') }}</option>
                    <option value="size">{{ t()('files.sortSize') }}</option>
                </select>
            </div>
            <div class="folder-summary" aria-live="polite">
                <span><i class="fa fa-folder" aria-hidden="true"></i> {{ folderCount() }} {{ t()('files.folders') }}</span>
                <span><i class="fa fa-file" aria-hidden="true"></i> {{ fileCount() }} {{ t()('files.files') }}</span>
                @if (search()) {
                    <span class="filtered-count">{{ visibleEntries().length }} / {{ entries().length }} {{ t()('files.shown') }}</span>
                }
            </div>
            @if (uploadError()) {
                <div class="inline-message error-message" role="alert">{{ uploadError() }}</div>
            }
            <div class="file-list">
                @if (loading()) {
                    <div class="explorer-state" role="status">
                        <i class="fa fa-spinner fa-spin" aria-hidden="true"></i><strong>{{ t()('files.loading') }}</strong>
                    </div>
                } @else if (error()) {
                    <div class="explorer-state error-state" role="alert">
                        <i class="fa fa-triangle-exclamation" aria-hidden="true"></i>
                        <strong>{{ t()('files.loadError') }}</strong>
                        <p>{{ error() }}</p>
                        <p-button [label]="t()('files.retry')" icon="fa fa-refresh" severity="info" [text]="true" size="small" (onClick)="refresh()" />
                    </div>
                } @else if (!visibleEntries().length) {
                    <div class="explorer-state">
                        <i [class]="search() ? 'fa fa-search' : 'fa fa-folder-open'" aria-hidden="true"></i>
                        <strong>{{ t()(search() ? 'files.noMatches' : 'files.empty') }}</strong>
                        <p>{{ t()(search() ? 'files.search' : 'files.emptyDetail') }}</p>
                        @if (search()) {
                            <p-button [label]="t()('files.clearSearch')" severity="secondary" [text]="true" size="small" (onClick)="search.set('')" />
                        }
                    </div>
                } @else {
                    <table class="files-table">
                        <thead>
                            <tr>
                                <th scope="col">{{ t()('files.name') }}</th>
                                <th scope="col" class="size-column">{{ t()('files.size') }}</th>
                                <th scope="col" class="modified-column">{{ t()('files.modified') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            @for (entry of visibleEntries(); track entry.path) {
                                <tr [class.selected]="selectedFile()?.path === entry.path" [class.folder-row]="entry.type === 'directory'" [attr.data-path]="entry.path">
                                    <td>
                                        <button type="button" class="entry-button" [attr.aria-pressed]="entry.type === 'file' ? selectedFile()?.path === entry.path : null" [disabled]="busy()" (click)="openEntry(entry)" [title]="entry.name">
                                            <span class="entry-icon"><i [class]="getFileIcon(entry)" aria-hidden="true"></i></span>
                                            <span class="entry-copy"
                                                ><strong>{{ entry.name }}</strong
                                                ><small>{{ entry.isSymlink ? t()('files.symlink') : entry.type === 'directory' ? t()('files.folder') : entry.extension || t()('files.file') }}</small></span
                                            >
                                            @if (entry.type === 'directory' && !entry.isSymlink) {
                                                <i class="fa fa-angle-right entry-chevron" aria-hidden="true"></i>
                                            }
                                        </button>
                                    </td>
                                    <td class="size-column">{{ entry.type === 'file' ? formatFileSize(entry.sizeBytes) : '—' }}</td>
                                    <td class="modified-column">{{ formatDate(entry.modifiedUtc) }}</td>
                                </tr>
                            }
                        </tbody>
                    </table>
                }
            </div>
            <footer class="browser-footer" aria-live="polite">
                <i [class]="uploading() ? 'fa fa-spinner fa-spin' : 'fa fa-cloud-arrow-up'" aria-hidden="true"></i>
                <span>{{ uploading() ? uploadStatus() : t()('files.dropHint') }}</span>
            </footer>
        </section>

        <aside class="inspector-pane" [class.has-file]="selectedFile()" [attr.aria-label]="t()('files.preview')" [attr.aria-busy]="previewLoading()">
            @if (selectedFile(); as file) {
                <header class="inspector-header">
                    <div class="inspector-title">
                        <i [class]="getFileIcon(file)" aria-hidden="true"></i><strong [title]="file.name">{{ file.name }}</strong>
                    </div>
                    <div class="inspector-actions">
                        @if (file.type === 'file') {
                            <p-button icon="fa fa-download" severity="success" [text]="true" size="small" [ariaLabel]="t()('files.download')" [pTooltip]="t()('files.download')" [loading]="downloading()" (onClick)="downloadFile(file)" />
                            <p-button icon="fa fa-trash" severity="danger" [text]="true" size="small" [ariaLabel]="t()('files.delete')" [pTooltip]="t()('files.delete')" [disabled]="busy()" [loading]="deleting()" (onClick)="deleteFile(file)" />
                        }
                        <p-button
                            [icon]="previewExpanded() ? 'fa fa-compress' : 'fa fa-expand'"
                            severity="info"
                            [text]="true"
                            size="small"
                            [ariaLabel]="t()(previewExpanded() ? 'files.collapse' : 'files.expand')"
                            [pTooltip]="t()(previewExpanded() ? 'files.collapse' : 'files.expand')"
                            (onClick)="previewExpanded.set(!previewExpanded())"
                        />
                        <p-button icon="fa fa-times" severity="secondary" [text]="true" size="small" [ariaLabel]="t()('files.close')" [pTooltip]="t()('files.close')" [disabled]="busy()" (onClick)="closePreview()" />
                    </div>
                </header>
                <dl class="file-details">
                    <div class="file-path">
                        <dt>{{ t()('files.path') }}</dt>
                        <dd>{{ file.path }}</dd>
                    </div>
                    <div>
                        <dt>{{ t()('files.size') }}</dt>
                        <dd>{{ formatFileSize(fileInfo()?.sizeBytes ?? file.sizeBytes) }}</dd>
                    </div>
                    <div>
                        <dt>{{ t()('files.modified') }}</dt>
                        <dd>{{ formatDate(fileInfo()?.modifiedUtc || file.modifiedUtc) }}</dd>
                    </div>
                    <div>
                        <dt>{{ t()('files.type') }}</dt>
                        <dd>{{ fileInfo()?.mimeType || file.extension || t()(file.type === 'directory' ? 'files.folder' : 'files.file') }}</dd>
                    </div>
                    @if (fileInfo()?.language) {
                        <div>
                            <dt>{{ t()('files.language') }}</dt>
                            <dd>{{ fileInfo()?.language }}</dd>
                        </div>
                    }
                </dl>
                @if (file.isSymlink) {
                    <div class="inline-message">{{ t()('files.symlinkHint') }}</div>
                }
                @if (fileInfo()?.binaryWarning || previewContent()?.warning) {
                    <div class="inline-message warning-message" role="status">{{ fileInfo()?.binaryWarning || previewContent()?.warning }}</div>
                }
                @if (previewContent()?.isTruncated) {
                    <div class="inline-message warning-message" role="status">{{ t()('files.truncated') }} ({{ formatFileSize(previewContent()?.truncatedAt) }} / {{ formatFileSize(fileInfo()?.sizeBytes) }})</div>
                }

                <div class="preview-content">
                    @if (previewLoading()) {
                        <div class="explorer-state" role="status">
                            <i class="fa fa-spinner fa-spin" aria-hidden="true"></i><strong>{{ t()('files.loadingPreview') }}</strong>
                        </div>
                    } @else if (previewError()) {
                        <div class="explorer-state error-state" role="alert">
                            <i class="fa fa-triangle-exclamation" aria-hidden="true"></i><strong>{{ t()('files.previewError') }}</strong>
                            <p>{{ previewError() }}</p>
                        </div>
                    } @else if (fileInfo()?.previewType === 'text' && previewContent()?.content != null) {
                        <div class="editor-toolbar">
                            @if (editMode()) {
                                <span class="editor-status" [class.dirty]="dirty()"><i class="fa fa-circle" aria-hidden="true"></i>{{ t()(dirty() ? 'files.dirty' : 'files.edit') }}</span>
                                <p-button icon="fa fa-floppy-disk" [label]="t()('files.save')" severity="success" size="small" [disabled]="!dirty() || busy()" [loading]="editSaving()" (onClick)="saveFile()" />
                                <p-button [label]="t()('files.cancel')" severity="secondary" [text]="true" size="small" [disabled]="busy()" (onClick)="cancelEdit()" />
                            } @else {
                                <span class="editor-status">{{ t()('files.preview') }}</span>
                                <p-button
                                    icon="fa fa-pencil"
                                    severity="warn"
                                    [text]="true"
                                    size="small"
                                    [ariaLabel]="t()('files.edit')"
                                    [pTooltip]="t()(canEdit() ? 'files.edit' : 'files.readOnly')"
                                    [disabled]="!canEdit() || busy()"
                                    (onClick)="enterEditMode()"
                                />
                            }
                        </div>
                        @if (editMode()) {
                            <textarea class="file-editor" [ngModel]="editContent()" (ngModelChange)="editContent.set($event)" [disabled]="editSaving()" [attr.aria-label]="t()('files.edit')" spellcheck="false"></textarea>
                        } @else {
                            <div class="code-container">
                                <div class="line-numbers" aria-hidden="true">
                                    @for (number of lineNumbers(); track number) {
                                        <span>{{ number }}</span>
                                    }
                                </div>
                                <pre class="code-block"><code [innerHTML]="highlightedCode()"></code></pre>
                            </div>
                        }
                    } @else if (imageUrl(); as src) {
                        <div class="image-preview"><img [src]="src" [alt]="file.name" /></div>
                    } @else if (pdfUrl(); as src) {
                        <iframe class="pdf-preview" [src]="src" [title]="file.name"></iframe>
                    } @else {
                        <div class="explorer-state">
                            <i class="fa fa-file-circle-question" aria-hidden="true"></i><strong>{{ t()('files.noPreview') }}</strong>
                            @if (file.type === 'file') {
                                <p>{{ t()('files.downloadHint') }}</p>
                            }
                        </div>
                    }
                </div>
            } @else {
                <div class="explorer-state inspector-empty">
                    <span class="empty-preview-icon"><i class="fa fa-file-lines" aria-hidden="true"></i></span><strong>{{ t()('files.selectFile') }}</strong>
                    <p>{{ t()('files.selectFileDetail') }}</p>
                </div>
            }
        </aside>
    </div>
</section>
